import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { CheckCircle, Save, X, UserPlus } from 'lucide-react';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import DateRangeFilter from '../components/DateRangeFilter';
import { useLanguage } from '../context/LanguageContext';
import { PAID_TYPES, UNPAID_TYPES, AUTO_PTO, mapRequestType } from '../absenceTypes';

// Solicitudes que el manager APROBO en la app de ausencias (abs_request). Aqui solo se leen; lo que
// entra al calculo de horas es lo que se "agrega al sistema", que crea las filas en absences_ctrl_hrs.
export default function AbsenceRequests({ onApplied }) {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeId, setTypeId] = useState('');
  const [onlyPending, setOnlyPending] = useState(true);

  const [applying, setApplying] = useState(null); // la solicitud abierta en el modal
  const [form, setForm] = useState({ id_emp_abs: '', type_abs: '', pto_abs: 0 });
  const [empLabel, setEmpLabel] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [empResults, setEmpResults] = useState([]);
  const [saving, setSaving] = useState(false);
  // Alta rapida de empleado desde el modal, para quien pidio vacaciones y aun no esta dado de alta.
  // Usa el mismo POST /api/employees que la pantalla de Empleados.
  const [newEmp, setNewEmp] = useState(null);
  const [creatingEmp, setCreatingEmp] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/absences/requests', { params: { employee: search, dateFrom, dateTo, typeId } });
      setRows(res.data.data);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('absReq.fetchError') });
    }
    setLoading(false);
  }, [search, dateFrom, dateTo, typeId, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get('/api/absences/requests/types').then(res => setTypes(res.data)).catch(() => setTypes([]));
  }, []);

  const openApply = (r) => {
    const type = mapRequestType(r.absence_type);
    setApplying(r);
    setForm({ id_emp_abs: r.employee?.id_emp || '', type_abs: type, pto_abs: AUTO_PTO[type] ?? 8 });
    setEmpLabel(r.employee ? `${r.employee.name_emp} ${r.employee.last_name_emp} · Badge ${r.employee.badge_num_emp}` : '');
    setEmpSearch('');
    setEmpResults([]);
    setNewEmp(null);
  };

  // Precarga lo que ya trae la solicitud; badge, ubicacion y tipo no los conoce la otra app.
  const openNewEmp = () => setNewEmp({
    badge_num_emp: '', name_emp: applying.first_name || '', last_name_emp: applying.last_name || '',
    department_emp: applying.department || '', company_emp: '', location_emp: '', type_emp: '',
    shift_start: '07:00', shift_end: '15:30',
  });

  const handleCreateEmployee = async () => {
    // Ubicacion y tipo son obligatorios aqui aunque la pantalla de Empleados no los pida: el calculo
    // cruza la ausencia por badge + ubicacion, y solo un PERM cobra el PTO.
    if (!newEmp.badge_num_emp || !newEmp.name_emp || !newEmp.last_name_emp || !newEmp.location_emp || !newEmp.type_emp) {
      Swal.fire({ icon: 'warning', title: t('absReq.newEmpMissing'), text: t('absReq.newEmpMissingMsg') });
      return;
    }
    setCreatingEmp(true);
    try {
      const res = await axios.post('/api/employees', newEmp);
      setForm(f => ({ ...f, id_emp_abs: res.data.id_emp }));
      setEmpLabel(`${newEmp.name_emp} ${newEmp.last_name_emp} · Badge ${newEmp.badge_num_emp}`);
      setNewEmp(null);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setCreatingEmp(false);
  };

  const searchEmployees = async (query) => {
    setEmpSearch(query);
    if (!query || query.length < 2) { setEmpResults([]); return; }
    try {
      const res = await axios.get('/api/employees', { params: { search: query } });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setEmpResults(list.slice(0, 8));
    } catch { setEmpResults([]); }
  };

  const selectEmployee = (emp) => {
    setForm(f => ({ ...f, id_emp_abs: emp.id_emp }));
    setEmpLabel(`${emp.name_emp} ${emp.last_name_emp} · Badge ${emp.badge_num_emp}`);
    setEmpSearch('');
    setEmpResults([]);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.id_emp_abs || !form.type_abs) return;
    setSaving(true);
    try {
      await axios.post(`/api/absences/requests/${applying.id}/apply`, form);
      setApplying(null);
      fetchData();
      if (onApplied) onApplied();
      Swal.fire({ icon: 'success', title: t('absReq.applied'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(String(d).substring(0, 10) + 'T00:00:00');
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  };

  const shown = onlyPending ? rows.filter(r => !r.applied) : rows;

  const columns = [
    {
      header: t('absReq.requester'),
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.first_name} {r.last_name}</div>
          <small style={{ color: 'var(--text-muted)' }}>{r.email}</small>
        </div>
      ),
    },
    { header: t('abs.dept'), accessor: 'department' },
    { header: t('common.type'), accessor: 'absence_type' },
    { header: t('abs.startDateCol'), render: (r) => formatDate(r.start_date) },
    { header: t('abs.endDateCol'), render: (r) => formatDate(r.end_date) },
    { header: t('absReq.weekdays'), render: (r) => r.weekdays },
    {
      header: t('absReq.approvedBy'),
      render: (r) => (
        <div>
          <div>{r.approved_by || r.manager || '-'}</div>
          {r.decided_at && <small style={{ color: 'var(--text-muted)' }}>{formatDate(r.decided_at)}</small>}
        </div>
      ),
    },
    {
      header: t('absReq.employee'),
      render: (r) => r.employee
        ? <span><span className="badge badge-info" style={{ marginRight: 6 }}>Badge {r.employee.badge_num_emp}</span>{r.employee.name_emp} {r.employee.last_name_emp}</span>
        : <span style={{ color: 'var(--danger)' }}>{t('absReq.unlinked')}</span>,
    },
    {
      header: t('common.status'),
      render: (r) => r.applied
        ? <span className="badge badge-success">{t('absReq.inSystem')}</span>
        : <span className="badge badge-warning">{t('absReq.pending')}</span>,
    },
    {
      header: t('common.actions'),
      style: { width: 130 },
      render: (r) => r.applied
        ? <small style={{ color: 'var(--text-muted)' }}>{r.applied_type} · {r.applied_pto} h/{t('absReq.day')}</small>
        : <button className="btn-primary btn-sm" style={{ width: 'auto' }} onClick={() => openApply(r)}>
            <CheckCircle size={14} style={{ marginRight: 4 }} />{t('absReq.apply')}
          </button>,
    },
  ];

  const totalHrs = applying ? (Number(form.pto_abs) || 0) * applying.weekdays : 0;

  return (
    <div>
      <div className="filters-bar">
        <SearchBar value={search} onChange={setSearch} placeholder={t('absReq.search')} />
        <select className="form-input" style={{ width: 'auto' }} value={typeId} onChange={e => setTypeId(e.target.value)}>
          <option value="">{t('absReq.allTypes')}</option>
          {types.map(ty => <option key={ty.id} value={ty.id}>{ty.name}</option>)}
        </select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onDateFromChange={setDateFrom} onDateToChange={setDateTo}
          onRefresh={() => { setDateFrom(''); setDateTo(''); fetchData(); }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} />
          {t('absReq.onlyPending')}
        </label>
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={shown} loading={loading} emptyMessage={t('absReq.empty')} />
      </div>

      <Modal isOpen={!!applying} onClose={() => setApplying(null)} title={t('absReq.applyTitle')}>
        {applying && (
          <form onSubmit={handleApply}>
            <div className="modal-body">
              <div style={{ padding: 12, marginBottom: 16, background: 'var(--bg-tertiary)', borderRadius: 'var(--radius)', fontSize: 14 }}>
                <div style={{ fontWeight: 600 }}>{applying.first_name} {applying.last_name} · {applying.absence_type}</div>
                <div>{formatDate(applying.start_date)} – {formatDate(applying.end_date)} · {applying.weekdays} {t('absReq.weekdaysLower')}</div>
                {applying.reason && <div style={{ color: 'var(--text-secondary)', marginTop: 4 }}>{applying.reason}</div>}
                {applying.comment && <div style={{ color: 'var(--text-secondary)', marginTop: 4 }}>{t('absReq.managerComment')}: {applying.comment}</div>}
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">{t('abs.employee')}</label>
                {empLabel && (
                  <div style={{ marginBottom: 8 }}>
                    <span className="badge badge-success" style={{ fontSize: 12, padding: '4px 10px' }}>{empLabel}</span>
                    {applying.employee?.source === 'name' && form.id_emp_abs === applying.employee.id_emp &&
                      <small style={{ marginLeft: 8, color: 'var(--text-muted)' }}>{t('absReq.byName')}</small>}
                  </div>
                )}
                {!newEmp && (
                  <input className="form-input" placeholder={empLabel ? t('absReq.changeEmployee') : t('abs.employeeSearch')}
                    value={empSearch} onChange={e => searchEmployees(e.target.value)}
                    onBlur={() => setTimeout(() => setEmpResults([]), 200)} />
                )}
                {!newEmp && (
                  <button type="button" className="btn-secondary btn-sm" style={{ width: 'auto', marginTop: 8 }} onClick={openNewEmp}>
                    <UserPlus size={14} style={{ marginRight: 4 }} />{t('absReq.newEmp')}
                  </button>
                )}
                {newEmp && (
                  <div style={{ marginTop: 4, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('absReq.newEmpTitle')}</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">{t('emp.badgeNumber')} *</label>
                        <input className="form-input" type="number" value={newEmp.badge_num_emp}
                          onChange={e => setNewEmp({ ...newEmp, badge_num_emp: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('common.department')}</label>
                        <input className="form-input" value={newEmp.department_emp}
                          onChange={e => setNewEmp({ ...newEmp, department_emp: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">{t('emp.nameReq')}</label>
                        <input className="form-input" value={newEmp.name_emp}
                          onChange={e => setNewEmp({ ...newEmp, name_emp: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('emp.lastNameReq')}</label>
                        <input className="form-input" value={newEmp.last_name_emp}
                          onChange={e => setNewEmp({ ...newEmp, last_name_emp: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">{t('common.location')} *</label>
                        <select className="form-input" value={newEmp.location_emp}
                          onChange={e => setNewEmp({ ...newEmp, location_emp: e.target.value })}>
                          <option value="">{t('common.select')}</option>
                          <option value="MIA">MIA</option>
                          <option value="AUSWH">AUSWH</option>
                          <option value="MX">MX</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('common.type')} *</label>
                        <select className="form-input" value={newEmp.type_emp}
                          onChange={e => setNewEmp({ ...newEmp, type_emp: e.target.value })}>
                          <option value="">{t('common.select')}</option>
                          <option value="PERM">PERM</option>
                          <option value="PERM-HOUR">PERM-HOUR</option>
                          <option value="PERM-SALARY">PERM-SALARY</option>
                          <option value="TEMP">TEMP</option>
                          <option value="CONTR">CONTR</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('common.company')}</label>
                        <input className="form-input" value={newEmp.company_emp}
                          onChange={e => setNewEmp({ ...newEmp, company_emp: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">{t('emp.shiftStart')}</label>
                        <input className="form-input" type="time" value={newEmp.shift_start}
                          onChange={e => setNewEmp({ ...newEmp, shift_start: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('emp.shiftEnd')}</label>
                        <input className="form-input" type="time" value={newEmp.shift_end}
                          onChange={e => setNewEmp({ ...newEmp, shift_end: e.target.value })} />
                      </div>
                    </div>
                    {newEmp.type_emp && !newEmp.type_emp.startsWith('PERM') && (
                      <small style={{ color: 'var(--warning)', display: 'block', marginBottom: 8 }}>{t('absReq.newEmpNotPerm')}</small>
                    )}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setNewEmp(null)}>{t('common.cancel')}</button>
                      <button type="button" className="btn-primary btn-sm" style={{ width: 'auto' }} disabled={creatingEmp} onClick={handleCreateEmployee}>
                        <Save size={14} style={{ marginRight: 4 }} />{creatingEmp ? t('common.saving') : t('absReq.newEmpSave')}
                      </button>
                    </div>
                  </div>
                )}
                {empResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', maxHeight: 220, overflowY: 'auto',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  }}>
                    {empResults.map(emp => (
                      <div key={emp.id_emp} onMouseDown={() => selectEmployee(emp)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600 }}>{emp.name_emp} {emp.last_name_emp}</span>
                        <span className="badge badge-info" style={{ fontSize: 11 }}>Badge {emp.badge_num_emp}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('abs.typeReq')}</label>
                  <select className="form-input" required value={form.type_abs} onChange={e => {
                    const type = e.target.value;
                    setForm({ ...form, type_abs: type, pto_abs: AUTO_PTO[type] ?? form.pto_abs });
                  }}>
                    <option value="">{t('common.select')}</option>
                    <optgroup label={t('abs.paid')}>
                      {PAID_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                    </optgroup>
                    <optgroup label={t('abs.unpaid')}>
                      {UNPAID_TYPES.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                    </optgroup>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('abs.ptoHrs')}</label>
                  <input className="form-input" type="number" min="0" step="any" value={form.pto_abs}
                    onChange={e => setForm({ ...form, pto_abs: Number(e.target.value) })} />
                </div>
              </div>

              <small style={{ color: 'var(--text-secondary)' }}>
                {t('absReq.summary').replace('{days}', applying.weekdays).replace('{hrs}', totalHrs)}
              </small>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setApplying(null)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }}
                disabled={saving || !!newEmp || !form.id_emp_abs || !form.type_abs || applying.weekdays === 0}>
                <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('absReq.apply')}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
